import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { MainSection } from '@/components/layout/MainSection';
import { InlineMessage } from '@/components/ui/InlineMessage';
import { SettingToggleRow } from '@/components/AdminSettings/SettingToggleRow';
import styles from './SettingsTab.module.scss';

interface AdminSettings {
  predictions_debug_mode: boolean;
  debug_console_enabled: boolean;
  auto_block_unverified_emails: boolean;
}

interface SettingsMessage {
  type: 'success' | 'error';
  text: string;
}

const SETTINGS_QUERY_KEY = ['admin-settings'] as const;

const QUICK_ACTIONS = [
  { to: '/tournaments', emoji: '🏆', titleKey: 'admin:quickActionTournaments', descKey: 'admin:quickActionTournamentsDesc' },
] as const;

const SettingsTab: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const [localSettings, setLocalSettings] = useState<AdminSettings>({
    predictions_debug_mode: false,
    debug_console_enabled: false,
    auto_block_unverified_emails: false,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState<SettingsMessage | null>(null);

  const {
    data: settingsData,
    isLoading: settingsLoading,
    error: settingsError,
  } = useQuery({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.getAdminSettings();
      return (response.data?.data || response.data) as AdminSettings;
    },
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (settingsData) {
      setLocalSettings(settingsData);
      setHasChanges(false);
    }
  }, [settingsData]);

  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: AdminSettings) => {
      const response = await apiClient.updateAdminSettings(settings);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
      setHasChanges(false);
      setMessage({ type: 'success', text: t('admin:settingsSaved') });
    },
    onError: (error: any) => {
      setMessage({
        type: 'error',
        text: t('admin:settingsSaveError', { error: error.response?.data?.error || error.message }),
      });
    },
  });

  const handleSettingChange = (key: keyof AdminSettings) => (value: boolean) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setMessage(null);
  };

  const handleResetSettings = () => {
    if (settingsData) {
      setLocalSettings(settingsData);
      setHasChanges(false);
    }
  };

  if (settingsLoading) return null;

  if (settingsError) {
    return (
      <MainSection title={t('admin:bpSettings')}>
        <div className={styles.wrapper}>
          <InlineMessage type="error" text={t('admin:settingsLoadError')} />
          <Link to="/" className={`ls-btn ${styles.homeLink}`}>
            {t('common:home')}
          </Link>
        </div>
      </MainSection>
    );
  }

  const isSaving = updateSettingsMutation.isPending;

  return (
    <MainSection title={t('admin:bpSettings')}>
      <div className={styles.wrapper}>
        {message && (
          <InlineMessage type={message.type} text={message.text} onDismiss={() => setMessage(null)} />
        )}

        <h2 className={styles.sectionHeader}>{t('admin:settingsDebugSection')}</h2>
        <div className={styles.group}>
          <SettingToggleRow
            id="predictions_debug_mode"
            title={t('admin:settingPredictionsDebugTitle')}
            description={t('admin:settingPredictionsDebugDesc')}
            tag={t('admin:settingsDebugOnlyTag')}
            warning={t('admin:settingPredictionsDebugWarning')}
            checked={localSettings.predictions_debug_mode}
            onChange={handleSettingChange('predictions_debug_mode')}
            disabled={isSaving}
          />
          <SettingToggleRow
            id="debug_console_enabled"
            title={t('admin:settingDebugConsoleTitle')}
            description={t('admin:settingDebugConsoleDesc')}
            tag={t('admin:settingsDebugOnlyTag')}
            warning={t('admin:settingDebugConsoleWarning')}
            checked={localSettings.debug_console_enabled}
            onChange={handleSettingChange('debug_console_enabled')}
            disabled={isSaving}
          />
        </div>

        <h2 className={styles.sectionHeader}>{t('admin:settingsAccountSection')}</h2>
        <div className={styles.group}>
          <SettingToggleRow
            id="auto_block_unverified_emails"
            title={t('admin:settingAutoBlockTitle')}
            description={t('admin:settingAutoBlockDesc')}
            checked={localSettings.auto_block_unverified_emails}
            onChange={handleSettingChange('auto_block_unverified_emails')}
            disabled={isSaving}
          />
        </div>

        {hasChanges && (
          <div className={styles.saveBar}>
            <span className={styles.unsaved}>{t('admin:settingsUnsaved')}</span>
            <div className={styles.saveActions}>
              <button
                type="button"
                onClick={handleResetSettings}
                disabled={isSaving}
                className={`ls-btn ls-btn--outlined ${styles.saveBtn}`}
                data-testid="settings-cancel"
              >
                {t('common:cancel')}
              </button>
              <button
                type="button"
                onClick={() => updateSettingsMutation.mutate(localSettings)}
                disabled={isSaving}
                className={`ls-btn ${styles.saveBtn}`}
                data-testid="settings-save"
              >
                {t('common:save')}
              </button>
            </div>
          </div>
        )}

        <h2 className={styles.sectionHeader}>{t('admin:settingsQuickActions')}</h2>
        <nav className={styles.group}>
          {QUICK_ACTIONS.map((action) => (
            <Link key={action.to} to={action.to} className={styles.navRow}>
              <span className={styles.navEmoji} aria-hidden="true">
                {action.emoji}
              </span>
              <span className={styles.navText}>
                <span className={styles.navTitle}>{t(action.titleKey)}</span>
                <span className={styles.navDesc}>{t(action.descKey)}</span>
              </span>
              <i className={`icon-chevron-right ${styles.navChevron}`} aria-hidden="true" />
            </Link>
          ))}
        </nav>
      </div>
    </MainSection>
  );
};

export default SettingsTab;
